/**
 * useSessionMessages — server-backed session + message history.
 *
 * Active session selection:
 *   * resume_mode ``device`` — localStorage pointer (per-device).
 *   * resume_mode ``latest`` — first row from GET /sessions (updated_at DESC).
 *   * ?session=ulid deep-link overrides on cold start.
 *
 * Message content is server-only; localStorage stores the opaque session id.
 */
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import type { Client } from "@hey-api/client-fetch";
import { createSession, listSessionMessages, listSessions, type ChatMessageOut } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/useAuth";
import { apiErrorMessage } from "@/lib/api-error";
import { useChatPrefs } from "@/hooks/useChatPrefs";
import type { SessionSummary } from "@/hooks/useSessionList";

export const ACTIVE_SESSION_KEY = "omni:active-session-id";
const PAGE_SIZE = 50;

interface SessionHistoryCache {
  sessionId: string;
  messages: SessionMessage[];
  hasMore: boolean;
  oldestId: string | null;
}
let _historyCache: SessionHistoryCache | null = null;

/** Test-only — clears the module-level message cache between cases. */
export function __resetSessionMessagesCacheForTests(): void {
  _historyCache = null;
}

interface MessagesPage {
  messages: SessionMessage[];
  hasMore: boolean;
}

const toMessage = (m: ChatMessageOut): SessionMessage => ({
  id: m.id,
  role: m.role as SessionMessage["role"],
  text: m.content,
  correlation_id: m.correlation_id,
  meta: m.metadata,
});

/** One page of a session's messages; ``null`` only when the session does
 *  not exist (404). Any other failure throws.
 *
 *  Both used to return "nothing" on *any* error — a 500 or a dropped
 *  connection looked like "this chat is gone", so loading forgot the
 *  stored chat and opened a new empty one (found porting to the generated
 *  client, RW-1.18). Now only a 404 means gone. */
async function _fetchMessages(
  client: Client,
  sessionId: string,
  beforeId?: string,
): Promise<MessagesPage | null> {
  const res = await listSessionMessages({
    client,
    path: { session_id: sessionId },
    query: { limit: PAGE_SIZE, ...(beforeId ? { before_id: beforeId } : {}) },
  });
  if (res.response.status === 404) return null;
  if (res.error !== undefined || !res.data) throw new Error(apiErrorMessage(res.error));
  return { messages: res.data.messages.map(toMessage), hasMore: res.data.has_more };
}

async function _createSession(client: Client): Promise<string> {
  const { data } = await createSession({ client, body: { title: null }, throwOnError: true });
  return data.id;
}

function _pickInitialSessionId(
  resumeMode: "device" | "latest",
  storedId: string | null,
  deepLinkId: string | null,
  sessions: SessionSummary[],
): string | null {
  if (deepLinkId) return deepLinkId;
  if (resumeMode === "device" && storedId) return storedId;
  const withMessages = sessions.filter((s) => s.message_count > 0);
  if (withMessages.length > 0) return withMessages[0].id;
  if (storedId) return storedId;
  return null;
}

export interface SessionMessage {
  id: string;
  role: "user" | "assistant" | "tool";
  text: string;
  correlation_id?: string | null;
  meta?: Record<string, unknown>;
}

export interface UseSessionMessagesResult {
  sessionId: string | null;
  messages: SessionMessage[];
  addMessage: (msg: SessionMessage) => void;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  newSession: () => Promise<void>;
  switchSession: (id: string) => Promise<void>;
  refreshMessages: () => Promise<void>;
}

export function useSessionMessages(): UseSessionMessagesResult {
  const auth = useAuth();
  const { prefs: chatPrefs, loading: chatPrefsLoading } = useChatPrefs();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const [messages, setMessages] = React.useState<SessionMessage[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(false);
  const oldestIdRef = React.useRef<string | null>(null);

  // A ref: the client is rebuilt when the token getter changes, and the
  // callbacks below must not re-run init() because of it.
  const client = useApiClient();
  const clientRef = React.useRef(client);
  clientRef.current = client;

  const applyPage = React.useCallback((id: string, page: MessagesPage): void => {
    setSessionId(id);
    setMessages(page.messages);
    setHasMore(page.hasMore);
    oldestIdRef.current = page.messages.length > 0 ? page.messages[0].id : null;
    localStorage.setItem(ACTIVE_SESSION_KEY, id);
  }, []);

  const loadSession = React.useCallback(
    async (targetId: string): Promise<boolean> => {
      const page = await _fetchMessages(clientRef.current, targetId);
      if (!page) return false;
      applyPage(targetId, page);
      return true;
    },
    [applyPage],
  );

  const init = React.useCallback(
    async (forceNewSession = false, explicitId?: string): Promise<void> => {
      setLoading(true);
      try {
        const deepLinkId = explicitId ?? searchParams.get("session");

        if (forceNewSession) {
          const newId = await _createSession(clientRef.current);
          localStorage.setItem(ACTIVE_SESSION_KEY, newId);
          setSessionId(newId);
          setMessages([]);
          setHasMore(false);
          oldestIdRef.current = null;
          _historyCache = null;
          if (searchParams.has("session")) {
            searchParams.delete("session");
            setSearchParams(searchParams, { replace: true });
          }
          return;
        }

        const { data: sessions } = await listSessions({ client: clientRef.current, throwOnError: true });
        const storedId = localStorage.getItem(ACTIVE_SESSION_KEY);
        const pickId = _pickInitialSessionId(
          chatPrefs.resume_mode,
          storedId,
          deepLinkId,
          sessions,
        );

        if (pickId) {
          const ok = await loadSession(pickId);
          if (ok) {
            if (deepLinkId) {
              searchParams.delete("session");
              setSearchParams(searchParams, { replace: true });
            }
            return;
          }
          if (storedId === pickId) localStorage.removeItem(ACTIVE_SESSION_KEY);
        }

        const newId = await _createSession(clientRef.current);
        localStorage.setItem(ACTIVE_SESSION_KEY, newId);
        setSessionId(newId);
        setMessages([]);
        setHasMore(false);
        oldestIdRef.current = null;
      } catch (err) {
        console.error("[useSessionMessages] init failed:", err);
        const fallbackId = localStorage.getItem(ACTIVE_SESSION_KEY);
        setSessionId(fallbackId && !fallbackId.startsWith("local_") ? fallbackId : null);
        setMessages([]);
      } finally {
        setLoading(false);
      }
    },
    [
      applyPage,
      chatPrefs.resume_mode,
      loadSession,
      searchParams,
      setSearchParams,
    ],
  );

  React.useEffect(() => {
    if (!auth.ready || !auth.user || chatPrefsLoading) return;
    const storedId = localStorage.getItem(ACTIVE_SESSION_KEY);
    if (_historyCache && storedId && _historyCache.sessionId === storedId) {
      setSessionId(_historyCache.sessionId);
      setMessages(_historyCache.messages);
      setHasMore(_historyCache.hasMore);
      oldestIdRef.current = _historyCache.oldestId;
      setLoading(false);
      return;
    }
    void init(false);
  }, [auth.ready, auth.user, chatPrefsLoading, chatPrefs.resume_mode, init]);

  React.useEffect(() => {
    if (!sessionId) return;
    _historyCache = {
      sessionId,
      messages,
      hasMore,
      oldestId: oldestIdRef.current,
    };
  }, [sessionId, messages, hasMore]);

  const addMessage = React.useCallback((msg: SessionMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  const loadMore = React.useCallback(async (): Promise<void> => {
    if (!sessionId || !hasMore || loadingMore || !oldestIdRef.current) return;
    setLoadingMore(true);
    try {
      const page = await _fetchMessages(clientRef.current, sessionId, oldestIdRef.current);
      if (!page) return;
      const older = page.messages;
      setMessages((prev) => [...older, ...prev]);
      setHasMore(page.hasMore);
      if (older.length > 0) oldestIdRef.current = older[0].id;
    } finally {
      setLoadingMore(false);
    }
  }, [sessionId, hasMore, loadingMore]);

  const switchSession = React.useCallback(
    async (id: string): Promise<void> => {
      if (id === sessionId) return;
      _historyCache = null;
      setLoading(true);
      try {
        const ok = await loadSession(id);
        if (!ok) throw new Error("session not found");
      } finally {
        setLoading(false);
      }
    },
    [sessionId, loadSession],
  );

  const refreshMessages = React.useCallback(async (): Promise<void> => {
    if (!sessionId) return;
    const page = await _fetchMessages(clientRef.current, sessionId);
    if (!page) return;
    setMessages(page.messages);
    setHasMore(page.hasMore);
    oldestIdRef.current =
      page.messages.length > 0 ? page.messages[0].id : null;
  }, [sessionId]);

  const newSession = React.useCallback(async () => {
    await init(true);
  }, [init]);

  return {
    sessionId,
    messages,
    addMessage,
    loading,
    loadingMore,
    hasMore,
    loadMore,
    newSession,
    switchSession,
    refreshMessages,
  };
}
